'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const PLANS = [
  {
    id: 'plan-free',
    name: 'Free Explorer',
    price: { monthly: 0, annual: 0 },
    description: 'Sample the platform with a curated set of free lessons.',
    cta: 'Get Started Free',
    ctaHref: '/lesson-library',
    highlighted: false,
    badge: null,
    features: [
      { text: '5 free lessons', included: true },
      { text: 'PDF downloads (free lessons only)', included: true },
      { text: 'Google Classroom assign (free lessons)', included: true },
      { text: 'Full lesson library access', included: false },
      { text: 'Editable Google Slides', included: false },
      { text: 'Canvas & Schoology integration', included: false },
      { text: 'Pacing guides & unit plans', included: false },
      { text: 'Early access to new lessons', included: false },
      { text: 'Teacher community access', included: false },
    ],
  },
  {
    id: 'plan-pro',
    name: 'Teacher Pro',
    price: { monthly: 14, annual: 10 },
    description: 'Full access for one teacher — the most popular plan.',
    cta: 'Start Teacher Pro',
    ctaHref: '/teacher-dashboard',
    highlighted: true,
    badge: 'Most Popular',
    features: [
      { text: 'All 340+ lessons', included: true },
      { text: 'Unlimited PDF downloads', included: true },
      { text: 'Google Classroom assign', included: true },
      { text: 'Full lesson library access', included: true },
      { text: 'Editable Google Slides', included: true },
      { text: 'Canvas & Schoology integration', included: true },
      { text: 'Pacing guides & unit plans', included: true },
      { text: 'Early access to new lessons', included: true },
      { text: 'Teacher community access', included: true },
    ],
  },
  {
    id: 'plan-school',
    name: 'School License',
    price: { monthly: 149, annual: 99 },
    description: 'Whole-school access with admin dashboard and bulk onboarding.',
    cta: 'Contact for Pricing',
    ctaHref: 'mailto:hello@teachglobal.edu',
    highlighted: false,
    badge: 'Best Value',
    features: [
      { text: 'All 340+ lessons — unlimited teachers', included: true },
      { text: 'Unlimited PDF downloads', included: true },
      { text: 'Google Classroom assign', included: true },
      { text: 'Full lesson library access', included: true },
      { text: 'Editable Google Slides', included: true },
      { text: 'Canvas & Schoology integration', included: true },
      { text: 'Pacing guides & unit plans', included: true },
      { text: 'Early access to new lessons', included: true },
      { text: 'Admin dashboard + usage reports', included: true },
    ],
  },
];

export default function PricingCards() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');

  return (
    <div className="bg-stone-50 py-16">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={`text-sm font-medium ${billing === 'monthly' ? 'text-stone-800' : 'text-stone-400'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBilling(billing === 'monthly' ? 'annual' : 'monthly')}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              billing === 'annual' ? 'bg-primary-500' : 'bg-stone-300'
            }`}
            role="switch"
            aria-checked={billing === 'annual'}
            aria-label="Toggle billing period"
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                billing === 'annual' ? 'translate-x-6' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billing === 'annual' ? 'text-stone-800' : 'text-stone-400'}`}>
            Annual
            <span className="ml-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full border border-emerald-200">
              Save 30%
            </span>
          </span>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {PLANS?.map((plan) => (
            <div
              key={plan?.id}
              className={`rounded-2xl border overflow-hidden ${
                plan?.highlighted
                  ? 'bg-primary-600 border-primary-700 shadow-2xl shadow-primary-500/20 scale-105'
                  : 'bg-white border-stone-200 shadow-card'
              }`}
            >
              {plan?.badge && (
                <div
                  className={`text-center py-2 text-xs font-bold uppercase tracking-wider ${
                    plan?.highlighted
                      ? 'bg-primary-500 text-white' :'bg-stone-100 text-stone-600'
                  }`}
                >
                  {plan?.badge}
                </div>
              )}

              <div className="p-6">
                <h3
                  className={`font-display font-bold text-xl mb-1 ${
                    plan?.highlighted ? 'text-white' : 'text-stone-800'
                  }`}
                >
                  {plan?.name}
                </h3>
                <p className={`text-sm mb-5 ${plan?.highlighted ? 'text-primary-200' : 'text-stone-500'}`}>
                  {plan?.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-end gap-1">
                    <span className={`text-4xl font-bold tabular-nums ${plan?.highlighted ? 'text-white' : 'text-stone-800'}`}>
                      ${plan?.price?.[billing]}
                    </span>
                    {plan?.price?.[billing] > 0 && (
                      <span className={`text-sm mb-1 ${plan?.highlighted ? 'text-primary-200' : 'text-stone-500'}`}>
                        /mo
                      </span>
                    )}
                  </div>
                  {billing === 'annual' && plan?.price?.annual > 0 && (
                    <p className={`text-xs mt-1 ${plan?.highlighted ? 'text-primary-200' : 'text-stone-400'}`}>
                      Billed ${plan?.price?.annual * 12}/year
                    </p>
                  )}
                </div>

                {/* CTA */}
                <Link
                  href={plan?.ctaHref}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold active:scale-95 transition-all duration-150 mb-6 ${
                    plan?.highlighted
                      ? 'bg-white text-primary-700 hover:bg-primary-50' :'bg-primary-500 text-white hover:bg-primary-600'
                  }`}
                >
                  {plan?.cta}
                </Link>

                {/* Features */}
                <ul className="space-y-2.5">
                  {plan?.features?.map((feature) => (
                    <li
                      key={`feat-${plan?.id}-${feature?.text}`}
                      className={`flex items-start gap-2.5 text-sm ${
                        feature?.included
                          ? plan?.highlighted ? 'text-primary-100' : 'text-stone-700' : plan?.highlighted ?'text-primary-400 opacity-50' : 'text-stone-400'
                      }`}
                    >
                      <Icon
                        name={feature?.included ? 'CheckCircleIcon' : 'XCircleIcon'}
                        variant={feature?.included ? 'solid' : 'outline'}
                        size={16}
                        className={`flex-shrink-0 mt-0.5 ${
                          feature?.included
                            ? plan?.highlighted ? 'text-primary-200' : 'text-accent-500' :'text-stone-300'
                        }`}
                      />
                      {feature?.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}